import { forwardRef, JSX, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './style.scss';
import Autocomplete, { type IAutocomplete, type IAutocompleteHistoryProps } from '../AutocompleteBase';
import type { IAutocompleteItem } from '../../../models/autocomplete-item.model';
import type { ILocation } from '../../../models/location';
import { LocationApiModel } from '../../../services/api-planera-resa/models/locations/location.api-model';
import { LocationType } from '../../../models/location-type.enum';
import { TranslationService } from '../../../services/translation.service';
import { GeolocationService, type Coordinates } from '../../../services/geolocation.service';
import type IValidatable from '../validatable.interface';
import ValidationInfo from '../../../validation/validation-info';
import type { IServerValidationError } from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';
import RequiredAsterisk from '../RequiredAsterisk';
import { DataSource } from '../../../hooks/fetch.hook';
import { LocationAutocompleteSelectionType } from './location-autocomplete-selection-type.enum';
import { AutocompleteLabelPosition, AutocompleteSuggestionSource } from '../AutocompleteBase/autocomplete-base.enum';
import PaginationResultApiModel from '../../../services/pagination-result.api-model';

export interface ILocationAutocompleteSelection extends IAutocompleteItem {
  type: LocationAutocompleteSelectionType;
  location?: ILocation;
  coordinates?: Coordinates;
}

export interface ILocationAutocompleteState {
  value: ILocationAutocompleteSelection | undefined;
  validationInfo: ValidationInfo;
}

interface ILocationAutocompleteProps {
  id: string;
  serverValidationName?: string;
  labelText?: string;
  labelClassName?: string;
  labelPosition?: AutocompleteLabelPosition;
  placeholderText?: string;
  required?: boolean;
  className?: string;
  noResultsText?: string;
  disabled?: boolean;
  historyProps?: IAutocompleteHistoryProps;
  locationTypes: LocationType[];
  enableCurrentPosition?: boolean;
  ariaDescribedBy?: string;
  showValidationMessages?: boolean;
  state: ILocationAutocompleteState;
  onSelect?: ((value: ILocationAutocompleteSelection | undefined) => void) | undefined;
  onKeyUp?: (() => void) | undefined;
  onFocus?: (() => void) | undefined;
}

function createResourceUrl(searchText: string, locationTypes: LocationType[]): string {
  const params = new URLSearchParams();
  params.append('q', searchText);
  locationTypes.forEach((locationType) => {
    params.append('types', locationType.toString());
  });
  return `/locations/by-text?${params.toString()}`;
}

function processReceivedData(data: unknown | undefined): IAutocompleteItem[] | undefined {
  return (
    (data as PaginationResultApiModel<LocationApiModel>)?.results.map(LocationApiModel.mapToBusinessModel).map(
      (x) =>
        ({
          type: LocationAutocompleteSelectionType.location,
          location: x,
        }) as ILocationAutocompleteSelection,
    ) ?? []
  );
}

function getItemId(item: IAutocompleteItem): string | undefined {
  const locationAutocompleteSelection = item as ILocationAutocompleteSelection;

  if (locationAutocompleteSelection?.type === LocationAutocompleteSelectionType.currentPosition) {
    return 'current-position';
  }

  if (locationAutocompleteSelection?.type === LocationAutocompleteSelectionType.location) {
    switch (locationAutocompleteSelection.location?.locationType) {
      case LocationType.stopArea:
      case LocationType.stopPoint:
        return locationAutocompleteSelection.location?.gid;
      case LocationType.pointOfInterest:
      case LocationType.address:
      case LocationType.metaStation:
        return `${locationAutocompleteSelection.location?.latitude}-${locationAutocompleteSelection.location?.longitude}`;
      default:
        return undefined;
    }
  }

  return undefined;
}

function getSuggestionId(componentId: string, item: IAutocompleteItem): string | undefined {
  return `autocomplete-result-item-${componentId}-${getItemId(item)}`;
}

function getDisplayName(item: IAutocompleteItem | undefined): string | undefined {
  if (!item) return undefined;
  const locationAutocompleteSelection = item as ILocationAutocompleteSelection;
  if (locationAutocompleteSelection?.type === LocationAutocompleteSelectionType.currentPosition) {
    return TranslationService.get('common.autocomplete.currentposition');
  }
  if (locationAutocompleteSelection?.type === LocationAutocompleteSelectionType.location) {
    return locationAutocompleteSelection.location?.name;
  }
  return undefined;
}

function calculateAriaDescribedBy(isInvalid: boolean, showValidationMessages: boolean, id: string, ariaDescribedBy: string | undefined): string | undefined {
  let result: string | undefined;
  if (isInvalid && showValidationMessages) {
    result = `${id}-error `;
  }
  if (ariaDescribedBy) {
    result = `${result ?? ''}${ariaDescribedBy}`;
  }
  return result?.trim();
}

export default forwardRef<IAutocomplete & IValidatable, ILocationAutocompleteProps>(function LocationAutocomplete(
  {
    id,
    serverValidationName = undefined,
    labelText,
    labelClassName = undefined,
    labelPosition = AutocompleteLabelPosition.above,
    placeholderText,
    required = false,
    className = undefined,
    noResultsText,
    disabled = false,
    historyProps = undefined,
    locationTypes,
    enableCurrentPosition = false,
    ariaDescribedBy = undefined,
    showValidationMessages = true,
    state,
    onSelect,
    onKeyUp,
    onFocus,
  }: ILocationAutocompleteProps,
  ref,
) {
  // === STATE ===
  const [searchText, setSearchText] = useState<string>('');
  const [geolocationPermitted, setGeolocationPermitted] = useState<boolean | undefined>(undefined);

  // === REFS ===
  const autocompleteRef = useRef<IAutocomplete>(null);

  // === EFFECTS ===
  useEffect(() => {
    if (enableCurrentPosition) {
      GeolocationService.getGeolocationPermission()
        .then((permission) => {
          setGeolocationPermitted(permission === 'granted');
        })
        .catch(() => {});
    }
  }, [enableCurrentPosition]);

  // === HELPER FUNCTIONS ===
  function calculateValidationMessageKey(): string {
    if (searchText?.length > 0) return 'common.validation.fromto.notselected';
    if (locationTypes.length === 1 && locationTypes[0] === LocationType.stopArea) {
      return 'common.validation.fromto.stoprequired';
    }
    if (locationTypes.length === 2 && locationTypes.indexOf(LocationType.stopArea) > -1 && locationTypes.indexOf(LocationType.metaStation) > -1) {
      return 'common.validation.fromto.stoprequired';
    }
    return 'common.validation.fromto.anyrequired';
  }

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    clear() {
      setSearchText('');
      autocompleteRef.current?.clear();
    },
    addSelectedItemToHistory(item: IAutocompleteItem) {
      if ((item as ILocationAutocompleteSelection).type === LocationAutocompleteSelectionType.currentPosition) return;
      autocompleteRef.current?.addSelectedItemToHistory(item);
    },
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo).requiredIf(required, state.value, calculateValidationMessageKey()).validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(serverValidationErrors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        serverValidationErrors
          .filter((e) => e.name === `${serverValidationName}Gid` || e.name === `${serverValidationName}Name`)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {},
      );
    },
  }));

  function createLeadingElement(suggestionId: string, isActive: boolean, onClick: () => void): JSX.Element {
    return (
      <li
        id={suggestionId}
        role="option"
        aria-selected={isActive}
        className={`autocomplete-result autocomplete-result__location d-flex align-items-center py-2 pe-2 m-0 w-100 border-x border-top current-position ${
          geolocationPermitted === undefined || geolocationPermitted ? '' : 'autocomplete-result--disabled'
        } ${isActive ? 'is-active' : ''}`}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick();
          }
        }}
      >
        <span>{TranslationService.get('common.autocomplete.usecurrentposition')}</span>
      </li>
    );
  }

  async function handleLeadingElementSelect(): Promise<void> {
    try {
      const permission = await GeolocationService.getGeolocationPermission();
      if (permission === 'denied') {
        throw new Error('vt.geolocation-permission-denied');
      }
      const coordinates = await GeolocationService.getCurrentPosition();
      onSelect?.({ type: LocationAutocompleteSelectionType.currentPosition, coordinates });
      setGeolocationPermitted(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e?.message === 'vt.geolocation-permission-denied' || e?.code === GeolocationPositionError.PERMISSION_DENIED) {
        setGeolocationPermitted(false);
        throw new Error(TranslationService.get('common.autocomplete.geolocationnotpermitted'), { cause: e });
      }
      setGeolocationPermitted(undefined);
      throw new Error(TranslationService.get('common.autocomplete.geolocationfailed'), { cause: e });
    }
  }

  function createSuggestionElement(suggestionId: string, item: IAutocompleteItem, isActive: boolean, suggestionSource: AutocompleteSuggestionSource, onClick: () => void): JSX.Element | null {
    if (!locationTypes.includes((item as ILocationAutocompleteSelection).location?.locationType ?? LocationType.unknown)) return null;

    function calculateLocationCssClass(_i: number, location: ILocation | undefined): string {
      if (!location) return 's';
      switch (location.locationType) {
        case LocationType.pointOfInterest:
          return 'p';
        case LocationType.address:
          return 'a';
        default:
          return 's';
      }
    }

    function calculateLocationTypeName(location: ILocation | undefined): string {
      if (!location) return '';
      if (location.locationType === LocationType.pointOfInterest) {
        return TranslationService.get('common.poi');
      }
      if (location.locationType === LocationType.address) {
        return TranslationService.get('common.streetaddress');
      }
      return TranslationService.get('common.stop');
    }

    return (
      <li
        id={suggestionId}
        key={suggestionId}
        role="option"
        aria-selected={isActive}
        className={`autocomplete-result autocomplete-result__location d-flex align-items-center py-2 px-2 m-0 w-100 border-x border-top ${isActive ? 'is-active' : ''} ${
          (item as ILocationAutocompleteSelection).type === LocationAutocompleteSelectionType.location ? calculateLocationCssClass(0, (item as ILocationAutocompleteSelection).location) : ''
        }`}
        onClick={onClick}
        onKeyDown={onClick}
      >
        {(suggestionSource === AutocompleteSuggestionSource.history || suggestionSource === AutocompleteSuggestionSource.commonsearches) && (
          <span className="visually-hidden">
            {TranslationService.get(suggestionSource === AutocompleteSuggestionSource.history ? 'common.autocomplete.recentsearch' : 'common.autocomplete.commonsearch')}
          </span>
        )}
        {(item as ILocationAutocompleteSelection).type === LocationAutocompleteSelectionType.location && (
          <span className="visually-hidden">{calculateLocationTypeName((item as ILocationAutocompleteSelection).location)}</span>
        )}
        <span className="d-block text-truncate">{getDisplayName(item)}</span>
      </li>
    );
  }

  return (
    <div>
      {labelPosition === AutocompleteLabelPosition.above && labelText && (
        <label id={`${id}-label`} htmlFor={id} className={labelClassName ?? ''}>
          {labelText}
          {required && <RequiredAsterisk />}
        </label>
      )}
      <Autocomplete
        selectedItem={state.value}
        searchText={searchText}
        id={id}
        ref={autocompleteRef}
        label={labelPosition === AutocompleteLabelPosition.inside ? labelText : undefined}
        placeholderText={placeholderText}
        required={required}
        noResultsText={noResultsText}
        className={className}
        disabled={disabled}
        historyProps={
          historyProps
            ? {
                ...historyProps,
                mapHistoryStringToItems: (s) => JSON.parse(s) as ILocationAutocompleteSelection[],
                mapItemsToHistoryString: (x) => JSON.stringify(x),
              }
            : undefined
        }
        isInvalid={state.validationInfo.isInvalid}
        ariaDescribedBy={calculateAriaDescribedBy(state.validationInfo.isInvalid, showValidationMessages, id, ariaDescribedBy)}
        listAriaLabelledBy={labelPosition === AutocompleteLabelPosition.above ? `${id}-label` : undefined}
        dataSource={DataSource.apiPlaneraResa}
        createResourceUrl={(st: string) => createResourceUrl(st, locationTypes)}
        processReceivedData={processReceivedData}
        getSuggestionId={getSuggestionId}
        getItemId={getItemId}
        getDisplayName={getDisplayName}
        createSuggestionElement={createSuggestionElement}
        onSelect={(item: IAutocompleteItem | undefined) => {
          if (item !== undefined) {
            setSearchText('');
          }
          onSelect?.(item as ILocationAutocompleteSelection | undefined);
          onKeyUp?.();
        }}
        getLeadingElementDisplayName={() => TranslationService.get('common.autocomplete.currentposition')}
        createLeadingElement={enableCurrentPosition ? createLeadingElement : undefined}
        onLeadingElementSelect={handleLeadingElementSelect}
        onSearchTextChange={(newSearchText: string) => {
          setSearchText(newSearchText);
          onKeyUp?.();
        }}
        onFocus={onFocus}
      />
      {showValidationMessages && state.validationInfo.isInvalid && (
        <div id={`${id}-error`} className={`invalid-feedback ${state.validationInfo.isInvalid ? 'd-block' : ''}`} aria-live="polite">
          {state.validationInfo.errors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}
    </div>
  );
});
